import { Controller } from "@hotwired/stimulus"

// INTENT: Remember the furthest heading a student has scrolled past on a study guide page so Resume can return them
// to it, and mark that saved spot with a bookmark in the table of contents and next to the heading.
// EXPECTED OUTCOME: Scrolling past a heading further down than the bookmark sends a debounced PATCH
// { position, section_anchor }; once saved, the bookmark moves down. Scrolling back up never moves it.
// CAVEAT/TRAP: Saving is best effort. Failures are swallowed so progress tracking can never interrupt reading.
export default class extends Controller {
  static targets = ["content", "toc", "bookmarkIcon"]
  static values = {
    url: String,
    position: Number,
    saved: String,
    // Wait for scrolling to settle before saving, so fast scrolling sends one request, not dozens.
    delay: { type: Number, default: 2000 },
    // A heading counts as "scrolled past" once its top is this close to the top of the viewport.
    offset: { type: Number, default: 120 }
  }

  connect() {
    this.headings = Array.from(this.contentTarget.querySelectorAll("h1[id], h2[id], h3[id]"))
    this.current = this.savedValue || null
    this.pending = false
    this.markBookmark(this.current)

    this.onScroll = this.onScroll.bind(this)
    this.flush = this.flush.bind(this)
    window.addEventListener("scroll", this.onScroll, { passive: true })
    // Save immediately when the student leaves mid-debounce (closing the tab, navigating away).
    window.addEventListener("pagehide", this.flush)
  }

  disconnect() {
    window.removeEventListener("scroll", this.onScroll)
    window.removeEventListener("pagehide", this.flush)
    if (this.frame) cancelAnimationFrame(this.frame)
    this.flush()
  }

  onScroll() {
    if (this.frame) return
    this.frame = requestAnimationFrame(() => {
      this.frame = null
      this.update()
    })
  }

  update() {
    const anchor = this.headingScrolledPast()
    // Forward-only: students must scroll back up to reach the page's navigation, so moving the bookmark back up
    // would effectively reset it every time they leave the page.
    if (this.headingIndex(anchor) <= this.headingIndex(this.current)) return

    this.current = anchor
    this.pending = true
    clearTimeout(this.timer)
    this.timer = setTimeout(() => this.save(), this.delayValue)
  }

  // The last heading whose top has passed the offset line; null while the student is still above the first heading.
  headingScrolledPast() {
    let anchor = null
    for (const heading of this.headings) {
      if (heading.getBoundingClientRect().top > this.offsetValue) break
      anchor = heading.id
    }
    return anchor
  }

  // -1 for no heading (top of the page) or a saved heading that no longer exists on the page.
  headingIndex(anchor) {
    return this.headings.findIndex((heading) => heading.id === anchor)
  }

  flush() {
    if (this.pending) this.save({ keepalive: true })
  }

  async save({ keepalive = false } = {}) {
    clearTimeout(this.timer)
    this.pending = false
    const anchor = this.current

    try {
      const response = await fetch(this.urlValue, {
        method: "PATCH",
        keepalive,
        headers: {
          accept: "application/json",
          "content-type": "application/json",
          "X-CSRF-Token": document.head.querySelector('meta[name="csrf-token"]')?.getAttribute("content") || ""
        },
        body: JSON.stringify({ position: this.positionValue, section_anchor: anchor })
      })
      if (response.ok) this.markBookmark(anchor)
    } catch (_error) {
      // Best effort: see CAVEAT/TRAP above.
    }
  }

  markBookmark(anchor) {
    this.element.querySelectorAll("[data-study-guide-bookmark]").forEach((node) => node.remove())
    if (this.hasTocTarget) {
      this.tocTarget.querySelectorAll('[aria-current="location"]').forEach((link) => link.removeAttribute("aria-current"))
    }
    if (!anchor || !this.hasBookmarkIconTarget) return

    // Compare ids directly instead of building a CSS selector, since heading ids come from content.
    const heading = this.headings.find((node) => node.id === anchor)
    if (heading) heading.prepend(this.bookmarkIcon())

    if (this.hasTocTarget) {
      const link = Array.from(this.tocTarget.querySelectorAll("a")).find((node) => node.getAttribute("href") === `#${anchor}`)
      if (link) {
        link.prepend(this.bookmarkIcon())
        link.setAttribute("aria-current", "location")
      }
    }
  }

  bookmarkIcon() {
    const icon = this.bookmarkIconTarget.content.firstElementChild.cloneNode(true)
    icon.dataset.studyGuideBookmark = ""
    return icon
  }
}
